import { lazy } from 'react';
import { createBrowserRouter } from 'react-router';
import { RootLayout } from './RootLayout';
import { HomePage } from './routes/HomePage';
import { NotFoundPage } from './routes/NotFoundPage';
import { LoginPage } from './routes/auth/LoginPage';
import { RegisterPage } from './routes/auth/RegisterPage';
import { ForgotPasswordPage, ResetPasswordPage } from './routes/auth/PasswordPages';
import { GuestOnly } from './routes/auth/GuestOnly';
import { DashboardLayout } from './dashboard/DashboardLayout';
import { BoardsPage } from './dashboard/BoardsPage';

// Тяжёлые страницы — отдельными чанками: движок (PixiJS) грузится только на доске.
const named = <K extends string>(load: () => Promise<Record<K, React.ComponentType<any>>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));

const BoardPage = named(() => import('./routes/BoardPage'), 'BoardPage');
const PublicBoardPage = named(() => import('./routes/BoardPage'), 'PublicBoardPage');
const LocalBoardPage = named(() => import('./routes/LocalBoardPage'), 'LocalBoardPage');
const InvitePage = named(() => import('./routes/InvitePage'), 'InvitePage');
const NotificationsPage = named(() => import('./routes/NotificationsPage'), 'NotificationsPage');
const ExplorePage = named(() => import('./routes/ExplorePage'), 'ExplorePage');
const TemplatesPage = named(() => import('./dashboard/TemplatesPage'), 'TemplatesPage');
const ProfileSettings = named(() => import('./settings/ProfileSettings'), 'ProfileSettings');
const WorkspaceSettings = named(() => import('./settings/WorkspaceSettings'), 'WorkspaceSettings');
const TokensSettings = named(() => import('./settings/TokensSettings'), 'TokensSettings');
const WebhooksSettings = named(() => import('./settings/WebhooksSettings'), 'WebhooksSettings');
const WebhookDetails = named(() => import('./settings/WebhooksSettings'), 'WebhookDetails');
const AdminOverview = named(() => import('./settings/admin/AdminPages'), 'AdminOverview');
const AdminUsers = named(() => import('./settings/admin/AdminPages'), 'AdminUsers');
const AdminBoards = named(() => import('./settings/admin/AdminPages'), 'AdminBoards');
const AdminInstance = named(() => import('./settings/admin/AdminPages'), 'AdminInstance');
const DevelopersPage = named(() => import('./routes/DevelopersPage'), 'DevelopersPage');

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/login', element: <GuestOnly><LoginPage /></GuestOnly> },
      { path: '/register', element: <GuestOnly><RegisterPage /></GuestOnly> },
      { path: '/forgot-password', element: <GuestOnly><ForgotPasswordPage /></GuestOnly> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/invite/:token', element: <InvitePage /> },
      { path: '/explore', element: <ExplorePage /> },
      {
        path: '/',
        element: <DashboardLayout />,
        children: [
          { path: 'dashboard', element: <BoardsPage view="all" /> },
          { path: 'dashboard/recent', element: <BoardsPage view="recent" /> },
          { path: 'dashboard/favorites', element: <BoardsPage view="favorites" /> },
          { path: 'dashboard/shared', element: <BoardsPage view="shared" /> },
          { path: 'dashboard/templates', element: <TemplatesPage /> },
          { path: 'dashboard/trash', element: <BoardsPage view="trash" /> },
          { path: 'dashboard/project/:projectId', element: <BoardsPage view="all" /> },
          { path: 'notifications', element: <NotificationsPage /> },
        ],
      },
      { path: '/settings/profile', element: <ProfileSettings /> },
      { path: '/settings/workspace/:id', element: <WorkspaceSettings /> },
      { path: '/settings/tokens', element: <TokensSettings /> },
      { path: '/settings/webhooks/:id', element: <WebhooksSettings /> },
      { path: '/settings/webhooks/:id/:webhookId', element: <WebhookDetails /> },
      { path: '/settings/admin', element: <AdminOverview /> },
      { path: '/settings/admin/users', element: <AdminUsers /> },
      { path: '/settings/admin/boards', element: <AdminBoards /> },
      { path: '/settings/admin/instance', element: <AdminInstance /> },
      { path: '/developers', element: <DevelopersPage /> },
      { path: '/board/:boardId', element: <BoardPage /> },
      { path: '/b/:slug', element: <PublicBoardPage /> },
      { path: '/embed/:slug', element: <PublicBoardPage embed /> },
      { path: '/local/:id', element: <LocalBoardPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
